"use client";

import { useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ChevronUp, Pause, Play, RotateCcw, RotateCw, X } from "lucide-react";

import { usePlayerStore } from "@/stores/player-store";
import PlaybackSpeedMenu from "./PlaybackSpeedMenu";
import Scrubber from "./Scrubber";
import { formatTime } from "@/lib/formatTime";

export const MINI_PLAYER_HEIGHT = 68;

function Cover({ src, alt }: { src?: string; alt: string }) {
  return src ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }}
    />
  ) : (
    <div
      aria-hidden
      style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        display: "grid",
        placeItems: "center",
        background: "color-mix(in srgb, currentColor 12%, transparent)",
      }}
    >
      ♪
    </div>
  );
}

export default function MiniPlayer() {
  const t = useTranslations("player");
  const locale = useLocale();
  const current = usePlayerStore((s) => s.current);
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const isBuffering = usePlayerStore((s) => s.isBuffering);
  const currentTime = usePlayerStore((s) => s.currentTime);
  const duration = usePlayerStore((s) => s.duration);
  const savedPos = usePlayerStore((s) =>
    s.current ? s.positionById[s.current.id] : 0,
  );
  const loadRequested = usePlayerStore((s) => s.loadRequested);
  const toggle = usePlayerStore((s) => s.toggle);
  const skip = usePlayerStore((s) => s.skip);
  const seek = usePlayerStore((s) => s.seek);
  const setExpanded = usePlayerStore((s) => s.setExpanded);
  const close = usePlayerStore((s) => s.close);

  // Reserve space so page content is never covered by the bar.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--player-h",
      `calc(${MINI_PLAYER_HEIGHT}px + env(safe-area-inset-bottom))`,
    );
    return () => {
      root.style.removeProperty("--player-h");
    };
  }, []);

  if (!current) return null;

  const shown = loadRequested ? currentTime : savedPos || currentTime;
  const resumable = !loadRequested && (savedPos ?? 0) > 5;

  const btn: React.CSSProperties = {
    display: "grid",
    placeItems: "center",
    width: 36,
    height: 36,
    borderRadius: 999,
    border: 0,
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
  };

  return (
    <div
      role="region"
      aria-label={t("region")}
      dir="ltr"
      style={{
        position: "fixed",
        insetInline: 0,
        bottom: 0,
        zIndex: 60,
        paddingBottom: "env(safe-area-inset-bottom)",
        background: "var(--background, #fff)",
        color: "var(--foreground, #111)",
        borderTop: "1px solid color-mix(in srgb, currentColor 14%, transparent)",
        boxShadow: "0 -6px 24px rgba(0,0,0,0.08)",
      }}
    >
      <Scrubber
        value={shown}
        duration={duration}
        disabled={!loadRequested || !duration}
        label={t("seek")}
        onSeek={seek}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          height: MINI_PLAYER_HEIGHT - 10,
          padding: "0 12px",
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label={t("expand")}
          style={{
            ...btn,
            width: "auto",
            height: "auto",
            gap: 10,
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            textAlign: "start",
          }}
        >
          <Cover src={current.poster} alt={current.title} />
          <span style={{ minWidth: 0, flex: 1 }}>
            {/* Layout stays LTR; each text run picks its own direction. */}
            <span
              dir="auto"
              style={{
                display: "block",
                fontSize: 14,
                fontWeight: 650,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "start",
                unicodeBidi: "plaintext",
              }}
            >
              {current.title}
            </span>
            <span
              dir="auto"
              style={{
                display: "block",
                fontSize: 12,
                opacity: 0.65,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textAlign: "start",
                unicodeBidi: "plaintext",
              }}
            >
              {resumable
                ? t("resumeAt", { time: formatTime(savedPos ?? 0, locale) })
                : current.artist}
            </span>
          </span>
        </button>

        <button type="button" style={btn} onClick={() => skip(-15)} aria-label={t("back15")}>
          <RotateCcw size={18} />
        </button>
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? t("pause") : t("play")}
          aria-busy={isBuffering}
          style={{
            ...btn,
            width: 44,
            height: 44,
            background: "currentColor",
            opacity: isBuffering ? 0.6 : 1,
          }}
        >
          <span style={{ color: "var(--background, #fff)", display: "grid" }}>
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </span>
        </button>
        <button type="button" style={btn} onClick={() => skip(15)} aria-label={t("forward15")}>
          <RotateCw size={18} />
        </button>
        <PlaybackSpeedMenu buttonStyle={btn} />
        <button type="button" style={btn} onClick={() => setExpanded(true)} aria-label={t("expand")}>
          <ChevronUp size={20} />
        </button>
        <button type="button" style={btn} onClick={close} aria-label={t("close")}>
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
